import { useCallback, useMemo } from 'react';

import { useMe } from '@hooks/useMe';
import { Piece, PieceAction, PieceNextAction } from '@rdTypes/apiContract';
import uniqBy from 'lodash/fp/uniqBy';
import { MailPieceInboxType, Roles } from 'src/types/shared';

type ForceActionData = {
  action: PieceAction;
  isAvailableForAllPieces: boolean;
};

type MailpieceActionsResult = {
  allActions: PieceNextAction[][];
  regularActions: PieceAction[];
  forceActions: PieceAction[];
  hasForceActions: boolean;
  availableForceActions: ForceActionData[];
  bulkRegularActions: PieceAction[];
};

export const useMailpieceActions = (pieces: Piece | Piece[], access: MailPieceInboxType): MailpieceActionsResult => {
  const { currentUser } = useMe();

  const filterUserActions = useCallback(
    (mailpiece: Piece): PieceNextAction[] => {
      if (!currentUser) return [];

      return mailpiece.users.reduce((acc: PieceNextAction[], user) => {
        const matchedUser = user.userId === currentUser.id;
        const matchedGroup = !!user.groupId && !!currentUser.groupIds?.includes(user.groupId);
        if (!matchedUser && !matchedGroup) return acc;

        const isMailroomAccess = user.access === Roles.Mailroom;
        const shouldInclude = access === MailPieceInboxType.Mailroom ? isMailroomAccess : !isMailroomAccess;

        return shouldInclude ? [...acc, ...user.nextActions] : acc;
      }, []);
    },
    [currentUser, access],
  );

  const categorizeActions = useCallback((nextActions: PieceNextAction[]) => {
    const forceActions: PieceAction[] = [];
    const regularActions: PieceAction[] = [];

    nextActions.forEach((nextAction) => {
      if (nextAction.pieceAction !== undefined) {
        if (nextAction.isForce) {
          forceActions.push(nextAction.pieceAction);
        } else {
          regularActions.push(nextAction.pieceAction);
        }
      }
    });

    return { forceActions, regularActions };
  }, []);

  const actions = useMemo(() => {
    const mailpieces = Array.isArray(pieces) ? pieces : [pieces];
    return mailpieces.map((mailpiece) => uniqBy('pieceAction', filterUserActions(mailpiece)));
  }, [pieces, filterUserActions]);

  const enhancedActions = useMemo((): MailpieceActionsResult => {
    const mailpieces = Array.isArray(pieces) ? pieces : [pieces];

    if (!mailpieces.length || !currentUser) {
      return {
        allActions: [],
        regularActions: [],
        forceActions: [],
        hasForceActions: false,
        availableForceActions: [],
        bulkRegularActions: [],
      };
    }

    // For a single piece
    if (mailpieces.length === 1) {
      const uniqueActions = uniqBy('pieceAction', filterUserActions(mailpieces[0]));
      const { forceActions, regularActions } = categorizeActions(uniqueActions);

      return {
        allActions: actions,
        regularActions,
        forceActions,
        hasForceActions: forceActions.length > 0,
        availableForceActions: forceActions.map((action) => ({
          action,
          isAvailableForAllPieces: true,
        })),
        bulkRegularActions: regularActions,
      };
    }

    // For multiple pieces - track action counts across all pieces
    const allForceActions = new Map<PieceAction, number>();
    const allRegularActions = new Map<PieceAction, number>();

    mailpieces.forEach((piece) => {
      const nextActions = filterUserActions(piece);
      const { forceActions, regularActions } = categorizeActions(nextActions);

      // Count occurrences of each action type
      forceActions.forEach((action) => {
        allForceActions.set(action, (allForceActions.get(action) || 0) + 1);
      });

      regularActions.forEach((action) => {
        allRegularActions.set(action, (allRegularActions.get(action) || 0) + 1);
      });
    });

    const availableForceActions = Array.from(allForceActions.entries()).map(([action, count]) => ({
      action,
      isAvailableForAllPieces: count === mailpieces.length,
    }));

    // For bulk regular actions, include actions available for ANY piece
    // The useGetBulkActionsConfig will handle disabling those not available for all pieces
    const bulkRegularActions = Array.from(allRegularActions.keys());

    return {
      allActions: actions,
      regularActions: [], // For bulk operations, use bulkRegularActions instead
      forceActions: Array.from(allForceActions.keys()),
      hasForceActions: allForceActions.size > 0,
      availableForceActions,
      bulkRegularActions,
    };
  }, [pieces, currentUser, actions, filterUserActions, categorizeActions]);

  return enhancedActions;
};
